import { Customer } from '../types/customers';
import { Receipt } from '../types/receipts';
import subMonth from 'date-fns/subMonths';
import eachDay from 'date-fns/eachDayOfInterval';

export const getCustomerName = (customer: Customer) => {
    const names = [customer.first_name, customer.last_name];
    return names.filter((x) => x).join(' ') || 'N/A';
};

export const getCustomerEmail = (customer: Customer) => {
    return customer.email || 'N/A';
};

export const getCustomerPhoneNumber = (customer: Customer) => {
    return customer.phone_number || 'N/A';
};

export const getCustomerType = (customer: Customer) => {
    return `customers.fields.customer_types.${customer.type}`;
};

export const getConsentType = (consentType: string) => {
    return `customers.fields.consent_types.${consentType}`;
};

export const getGender = (customer: Customer) => {
    return customer.gender ? `customers.fields.genders.${customer.gender}` : 'N/A';
};

export const getConsentValue = (consentValue: boolean | undefined) => {
    const dictKey = consentValue ? 'yes' : 'no';
    return `customers.fields.consent_values.${dictKey}`;
};

export interface MonthlyTransactions {
    month: number;
    year: number;
    total: number;
    sort: number;
    numTransactions: number;
}

export const getReceiptsCurrencies = (receipts: Receipt[]): string[] => {
    // Get unique list of currencies from receipts filtering out undefined
    return [
        ...new Set(
            receipts.map((r) => {
                return r.currency;
            })
        ),
    ].filter(Boolean) as string[];
};

export const getReceiptsByCurrency = (currency: string, receipts: Receipt[]): Receipt[] =>
    receipts.filter((receipt) => receipt.currency === currency);

const getTotalForMonth = (zeroIndexedMonth: Number, receipts: Receipt[]) => {
    return receipts.reduce((acc: number, receipt: Receipt) => {
        const { purchase_at } = receipt;
        // Note that Date().getMonth() returns a zero indexed month number
        const transactionMonth = new Date(purchase_at).getMonth();
        if (zeroIndexedMonth !== transactionMonth) return acc;
        return acc + (receipt.gross_amount || 0);
    }, 0);
};

const getNumberOfTransactionsInMonth = (zeroIndexedMonth: Number, receipts: Receipt[]) => {
    return (
        receipts.filter((receipt) => {
            const { purchase_at } = receipt;
            // Note that Date().getMonth() returns a zero indexed month number
            const transactionMonth = new Date(purchase_at).getMonth();
            return transactionMonth === zeroIndexedMonth;
        }).length || 0
    );
};

export const getAvgDaysBetweenPurchase = (receipts: Receipt[]) => {
    if (receipts.length === 0) return 0;
    const lastTransaction = receipts[0];
    const firstTransaction = receipts[receipts.length - 1];
    const daysBetweenFirstAndLast = eachDay({
        start: new Date(firstTransaction.purchase_at),
        end: new Date(lastTransaction.purchase_at),
    }).length;
    return Math.round(daysBetweenFirstAndLast / receipts.length);
};

export const getAggregatedReceiptsByMonth = (receipts: Receipt[]): MonthlyTransactions[] => {
    // Victory will detect sorting ascending/descending based on what you
    // pass as "x", so "month" won't work.
    // Also, the typings are not correct and props "sortOrder" and "sortKey"
    // will result in a TypeScript error.
    // See VictoryBarProps: (https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/victory/index.d.ts)
    // As a temporary solution, add "sort" and reverse array to so it's in correct order.
    const today = new Date();
    return [...Array(12)]
        .map((_, i) => {
            const month = subMonth(today, i).getMonth();
            const year = subMonth(today, i).getFullYear();
            const total = getTotalForMonth(month, receipts);
            const numTransactions = getNumberOfTransactionsInMonth(month, receipts);
            return {
                month,
                year,
                total,
                numTransactions,
                sort: 12 - i,
            };
        })
        .reverse();
};

export const getGeneratedRevenue = (receipts: Receipt[]): number => {
    return receipts.reduce((acc: number, receipt: Receipt) => {
        return acc + (receipt.gross_amount || 0);
    }, 0);
};

export const getNumTransactions = (receipts: Receipt[]): number => {
    return receipts.length;
};
